/*
 * Promo popup, step 2: optional SMS signup.
 *
 * Step 1 is the native Shopify newsletter form. It reloads the page with `customer_posted=true`
 * and popup-modal.js reopens the popup (`.with-form-response`). We only get to step 2 through that
 * reload, so the email typed in step 1 is stashed in sessionStorage on submit and read back here.
 *
 * Step 2 posts { email, phone, consent } as JSON to our backend (POST /api/v1/popup/sms-signup), which
 * puts the phone + SMS marketing consent on the Shopify customer created by step 1. Shopify is the
 * source of truth; Mailchimp gets the phone through its Shopify sync. The theme never talks to Mailchimp.
 */
(function () {
  const EMAIL_KEY = 'whiskPromoPopupEmail';

  function storage(fn) {
    // sessionStorage can throw (private mode, blocked cookies); step 2 just won't appear then
    try { return fn(window.sessionStorage); } catch (e) { return null; }
  }

  // Step 1: remember the email across the reload that follows the native form post
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('popup-modal')) return;
    const input = form.querySelector('input[name="contact[email]"]');
    if (input && input.value) storage((s) => s.setItem(EMAIL_KEY, input.value.trim()));
  });

  // US numbers only: 10 digits, or 11 starting with 1. Returns the 10 digits or null.
  function parseUsPhone(raw) {
    const digits = (raw || '').replace(/\D/g, '');
    if (digits.length === 10) return digits;
    if (digits.length === 11 && digits[0] === '1') return digits.slice(1);
    return null;
  }

  // POST the signup to the backend. Resolves to { ok, message }; never rejects, so the caller only
  // has to branch on ok. A non-JSON reply or a network failure/timeout gives the generic message.
  async function postSignup(apiUrl, payload) {
    const generic = { ok: false, message: 'Something went wrong. Please try again.' };
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(apiUrl.replace(/\/+$/, '') + '/api/v1/popup/sms-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      const data = await response.json();
      if (data && data.ok === true) return { ok: true };
      return { ok: false, message: (data && data.message) || generic.message };
    } catch (e) {
      return generic;
    } finally {
      clearTimeout(timer);
    }
  }

  function init() {
    const popup = document.querySelector('popup-modal.with-form-response');
    const step = popup && popup.querySelector('[data-sms-step]');
    if (!step) return;

    // Only continue after a successful email signup (the newsletter form marks its success message)
    const succeeded = popup.querySelector('.newsletter-form__input[aria-describedby$="-success"]');
    const email = storage((s) => s.getItem(EMAIL_KEY));
    if (!succeeded || !email) return;

    popup.classList.add('is-sms-step');
    step.hidden = false;

    const form = step.querySelector('[data-sms-form]');
    const phoneInput = step.querySelector('[data-sms-phone]');
    const consent = step.querySelector('[data-sms-consent]');
    const message = step.querySelector('[data-sms-message]');
    const submit = step.querySelector('[data-sms-submit]');

    function show(text, isError) {
      message.textContent = text;
      message.hidden = !text;
      message.classList.toggle('promo-popup-sms__message--error', !!isError);
    }

    // Tidy what the visitor typed ("+1 470-667-0887", "470.667.0887"...) into the placeholder format
    phoneInput.addEventListener('blur', () => {
      const phone = parseUsPhone(phoneInput.value);
      if (phone) phoneInput.value = `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      // Consent starts unticked on purpose: only an explicit tick may subscribe someone to SMS
      if (!consent.checked) return show('Please tick the box to agree to receive texts.', true);
      const phone = parseUsPhone(phoneInput.value);
      if (!phone) return show('Please enter a valid 10-digit US mobile number.', true);

      show('');
      submit.disabled = true;

      // E.164, as the backend expects. The message is shown with textContent (see show), never as HTML.
      const result = await postSignup(step.dataset.apiUrl, { email, phone: '+1' + phone, consent: true });
      if (result.ok) {
        storage((s) => s.removeItem(EMAIL_KEY));
        // Same dismissal as the X / "No thanks", so the popup doesn't come back on the next visit
        if (typeof popup.storeKey === 'function') popup.storeKey();
        form.hidden = true;
        step.querySelector('[data-sms-decline]').hidden = true;
        step.querySelector('[data-sms-success]').hidden = false;
        return;
      }
      submit.disabled = false;
      show(result.message, true);
    });
  }

  // popup-modal.js is async: wait for the element to be defined so `with-form-response` is set
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('popup-modal').then(init);
  }
})();
